import classNames from "classnames";
import React, {
    HTMLInputTypeAttribute, ReactElement, ReactNode, useCallback, useEffect, useRef, useState,
} from "react";
import { ButtonGroup, Dropdown } from "react-bootstrap";
import { GrFormClose } from "react-icons/gr";
import { HiOutlineVariable } from "react-icons/hi";
import {
    ActionContextType,
    AsyncObjectType, ChatStatus, ConversationSourceType, FieldTypedValueFileInput, FieldTypedValueInput, FormField,
    FormFieldOption, FormFieldOptionSettings, FormFieldReplacementInput, FormFieldType,
    FormType, TicketPriority,
    WorkflowFormFieldSchema,
} from "../../__generated__/api";
import { ThemeContextAction, useTheme } from "../../contexts/Theme";
import { toInputDateTime, toUTCInputDate, toUTCInputHourMinute } from "../../date";
import Attachment from "../attachment/Attachment";
import Confirm from "../confirm/Confirm";
import BooleanSelect from "../pickers/boolean/BooleanSelect";
import ChatStatusPicker from "../pickers/chat-status/ChatStatus";
import DeflectionSource from "../pickers/deflection-sources/DeflectionSource";
import DynamicPicker from "../pickers/dynamic-picker/DynamicPicker";
import EmailAliases from "../pickers/email-aliases/EmailAliases";
import PriorityPicker from "../pickers/priority/Priority";
import Queues from "../pickers/queue/Queue";
import RequestTypes from "../pickers/request-types/RequestTypes";
import SeveritiesPicker from "../pickers/severity/Severities";
import SlaTierPicker from "../pickers/sla-tiers/SlaTiers";
import Tags from "../pickers/tags/Tags";
import TicketStates from "../pickers/ticket-states/TicketStates";
import TimezoneSelect from "../pickers/timezone/TimezoneSelect";
import UserGroups from "../pickers/user-groups/UserGroups";
import Users from "../pickers/users/Users";
import { ReplacementGroup } from "../replacement-group";
import Select, { Option } from "../select/Select";
import Button from "../theme/button/Button";
import { createUserWorkflowFields } from "../workflow/WorkflowFields";
import WorkflowPicker from "../workflow/WorkflowPicker";
import Styles from "./EditableText.module.scss";
import JsonBuilder, { isRecord } from "./JsonBuilder";
import ConversationSources from "../pickers/conversation-sources/ConversationSources";
import ChannelTags from "../pickers/channel-tags/ChannelTags";
import Icon from "../theme/icon/Icon";
import TimePicker from "../pickers/time/TimePicker";
import Editor from "../agent-v3/editor/Editor";
import { santitize, StripFormatting } from "../html-render/HtmlRenderer";

export enum ControlType {
    TEXT,
    SEARCH_TEXT,
    LONG_TEXT,
    SELECT,
    MULTI_SELECT,
    MULTI_LEVEL_SELECT,
    MULTI_SELECT_TAGS,
    BOOLEAN,
    DATE,
    TIME,
    DATE_TIME,
    NUMBER,
    DECIMAL,
    USER,
    USERS,
    CHECKBOX_LIST,
    PASSWORD,
    ASYNC_DROPDOWN,
    JSON,
    WORKFLOW,
    WORKFLOW_TICKET,
    EMAIL,
    URL,
    FILES
}

export interface RecursiveOption<T> extends Option<T> {
    children?: RecursiveOption<T>[]
}

export type ValueTypes = string | string[] | Date | boolean | number
| Record<string, unknown>
| FieldTypedValueFileInput[]
| ReactElement;

interface ControlProps<T = never, TIsRequired extends boolean = false> {
    onChange?: (
        newString: ValueTypes | (TIsRequired extends false ? undefined : never),
        replacements?: FormFieldReplacementInput[], displayText?: string
    ) => void;
    value?: ValueTypes;
    children?: ReactElement | string;
    placeholder?: string;
    placeholderClassName?: string;
    maxLength?: number | undefined;

    isReadOnly?: boolean;
    type?: ControlType;
    jsonSchema?: WorkflowFormFieldSchema;

    options?: RecursiveOption<T>[];

    asyncObjectType?: AsyncObjectType;
    optionsSettings?: FormFieldOptionSettings;
    integrationId?: string;

    labels?: string[];
    isEditing?: boolean;
    isFormField?: boolean;

    shouldFocus?: boolean;
    inputClassName?: string;
    variableClassName?: string;
    isRequired?: boolean;
    replaceableInput?: ReplacementGroup[];
    replacements?: FormFieldReplacementInput[];
    canCreate?: boolean;
    isInputReadOnly?: boolean;
    shouldHandleEnter?: boolean;
    shouldUseEditor?: boolean;
    editIcon?: ReactNode,

    getCurrentForm?: () => FieldTypedValueInput[];

    replacementInsertLabel?: string
}

export const FORM_FIELD = {
    [FormFieldType.Boolean]: ControlType.BOOLEAN,
    [FormFieldType.Date]: ControlType.DATE,
    [FormFieldType.Time]: ControlType.TIME,
    [FormFieldType.DateTime]: ControlType.DATE_TIME,
    [FormFieldType.Decimal]: ControlType.DECIMAL,
    [FormFieldType.Number]: ControlType.NUMBER,
    [FormFieldType.Text]: ControlType.TEXT,
    [FormFieldType.LongText]: ControlType.LONG_TEXT,
    [FormFieldType.Dropdown]: ControlType.SELECT,
    [FormFieldType.AsyncDropdown]: ControlType.ASYNC_DROPDOWN,
    [FormFieldType.SearchText]: ControlType.SEARCH_TEXT,
    [FormFieldType.MultiLevelDropdown]: ControlType.MULTI_LEVEL_SELECT,
    [FormFieldType.MultiSelectDropdown]: ControlType.MULTI_SELECT,
    [FormFieldType.User]: ControlType.USER,
    [FormFieldType.Users]: ControlType.USERS,
    [FormFieldType.Checkboxes]: ControlType.CHECKBOX_LIST,
    [FormFieldType.Password]: ControlType.PASSWORD,
    [FormFieldType.Json]: ControlType.JSON,
    [FormFieldType.Workflow]: ControlType.WORKFLOW,
    [FormFieldType.WorkflowTicket]: ControlType.WORKFLOW_TICKET,
    [FormFieldType.Files]: ControlType.FILES,
    [FormFieldType.Url]: ControlType.URL,
    [FormFieldType.Email]: ControlType.EMAIL,
};

export const FORM_FIELD_TO_TYPE: Record<ControlType | number, HTMLInputTypeAttribute> = {
    [ControlType.DATE]: "date",
    [ControlType.TIME]: "time",
    [ControlType.DATE_TIME]: "datetime-local",
    [ControlType.NUMBER]: "number",
    [ControlType.DECIMAL]: "number",
    [ControlType.PASSWORD]: "password",
    [ControlType.EMAIL]: "email",
    [ControlType.URL]: "url",

    [ControlType.TEXT]: "text",
    [ControlType.SEARCH_TEXT]: "text",

};

const BLOCKED_TEXT_REPLACEMENTS = new Set<FormFieldType>([FormFieldType.User, FormFieldType.Users]);

function normalizeInput(input: string): string {
    return input.toLowerCase().replace(/\s/g, "_").replace(/[^a-zA-Z0-9_]/g, "");
}

function SelectLevel({
    index,
    labels,
    placeholder,
    options,
    current,
    setValue,
    onKeyDown,
    dynamicChoiceContext,
    integrationId,
    parentValues,
    getCurrentForm,
    optionsSettings,
    theme,
}: {
    index: number;
    labels: string[] | undefined;
    placeholder: string | undefined;
    options: RecursiveOption<unknown>[] | undefined;
    current: string[];
    setValue: (
        handler: ((index: number, current: ValueTypes, dynamicContexts: string[]) => ({
            value: string[];
            dynamicContexts?: string[]
        }))
    ) => void;
    onKeyDown: (event: React.KeyboardEvent<HTMLElement>) => void;
    dynamicChoiceContext?: string;
    integrationId?: string;
    parentValues?: string[];
    getCurrentForm?: () => FieldTypedValueInput[];
    optionsSettings?: FormFieldOptionSettings;
    theme?: ThemeContextAction;
}): ReactElement {
    const label = labels?.[index];

    return (
        <div key={`drop${index}`}>
            {label && (
                <small className={theme?.isV3 ? "form-sub-label" : "text-gray-500"}>
                    {label}
                </small>
            )}
            {dynamicChoiceContext ? (
                <DynamicPicker
                    onFirstLoad={(_text, newDynamicChoice) => {
                        setValue((valueIndex: number, currentValue: ValueTypes, dynamicContexts?: string[]) => {
                            const valueArray = Array.isArray(currentValue) ? currentValue as string[] : [];
                            const newDynamicContexts = Array.isArray(dynamicContexts) ? dynamicContexts.copy() as string[] : [];
                            if (newDynamicContexts) {
                                newDynamicContexts[valueIndex] = newDynamicChoice as string;
                            }
                            return { value: valueArray, dynamicContexts: newDynamicContexts };
                        });
                    }}
                    onChange={(newValue, _replace, _text, newDynamicChoice) => {
                        setValue((valueIndex: number, currentValue: ValueTypes, dynamicContexts?: string[]) => {
                            const valueArray = Array.isArray(currentValue) ? currentValue.copy() as string[] : [];
                            valueArray.splice(valueIndex, valueArray.length, ...(newValue as string[]));
                            // valueArray[valueIndex] = Array.isArray(newValue) ? newValue[0] as string : newValue as string;

                            const newDynamicContexts = Array.isArray(dynamicContexts) ? dynamicContexts.copy() as string[] : [];
                            if (newDynamicContexts) {
                                newDynamicContexts[valueIndex] = newDynamicChoice as string;
                            }
                            return { value: valueArray, dynamicContexts: newDynamicContexts };
                        });
                    }}
                    placeholder={placeholder}
                    value={Array.isArray(current) ? current : [current]}
                    parentValues={parentValues}
                    isRequired={false}
                    labels={undefined}
                    dynamicChoiceContext={dynamicChoiceContext}
                    optionsSettings={optionsSettings}
                    integrationId={integrationId as string}
                    type={ControlType.MULTI_LEVEL_SELECT}
                    getCurrentForm={getCurrentForm}
                    isFormField
                />
            ) : (
                <Select
                    onChange={(event: Option<unknown>) => {
                        setValue((valueIndex: number, currentValue: ValueTypes, dynamicContexts?: string[]) => {
                            const valueArray = Array.isArray(currentValue) ? currentValue.copy() as string[] : [];
                            valueArray[valueIndex] = event.value as string;

                            const newDynamicContexts = Array.isArray(dynamicContexts) ? dynamicContexts.copy() as string[] : [];
                            if (newDynamicContexts) {
                                newDynamicContexts[valueIndex] = (
                                    event.data as { dynamicChoiceContext: string }
                                )?.dynamicChoiceContext as string;
                            }

                            return {
                                value: valueArray,
                                dynamicContexts: newDynamicContexts,
                            };
                        });
                    }}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    value={options?.find(opt => opt.value === current[0])}
                    options={options ?? []}
                />
            )}

        </div>
    );
}

SelectLevel.defaultProps = {
    dynamicChoiceContext: undefined,
    integrationId: undefined,
    parentValues: undefined,
    getCurrentForm: undefined,
    optionsSettings: undefined,
    theme: undefined,
};

export default function EditableControl<T>(props: ControlProps<T>): ReactElement {
    const [value, setValue] = useState<ValueTypes>(() => props.value ?? "");
    const [htmlValue, setHtmlValue] = useState<string>(() => props.value as string ?? "");
    const [editorReplacements, setEditorReplacements] = useState<FormFieldReplacementInput[]>([]);

    const [isEditing, setIsEditing] = useState<boolean>(false);
    const [lastSaved, setLastSaved] = useState<ValueTypes>(() => props.value ?? "");
    const [searchTimeout, setSearchTimeout] = useState<ReturnType<typeof setTimeout>>();
    const [isShowReplace, setShowReplace] = useState<boolean>(false);
    const [replacements, setReplacements] = useState<FormFieldReplacementInput[]>(props.replacements ?? []);

    const [dynamicChoices, setDynamicChoices] = useState<string[]>([]);

    const ref = useRef<HTMLInputElement | HTMLTextAreaElement | null>();
    const theme = useTheme();

    const isRequired = !props.isFormField || props.isRequired;
    async function handleSubmit(evt?: React.FormEvent<HTMLFormElement>): Promise<void> {
        evt?.preventDefault();
        setIsEditing(false);
        setLastSaved(value);
        props.onChange?.(value, replacements);
    }

    useEffect(() => {
        setReplacements(props.replacements ?? []);
    }, [props.replacements]);
    useEffect(() => {
        setValue(props.value ?? "");
        setLastSaved(props.value ?? "");
    }, [props.value]);

    useEffect(() => {
        setIsEditing(props.isEditing ?? false);
    }, [props.isEditing]);

    useEffect(() => {
        if (isEditing && props.shouldFocus) {
            ref.current?.focus();
        }
    }, [ref, isEditing]);

    useEffect(() => {
        if (theme.isV3 && props.shouldUseEditor) {
            setHtmlValue(props.value as string ?? "");
        }
    }, [value != null, theme.isV3, props.shouldUseEditor]);

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.code === "Enter") {
            setIsEditing(true);
        } else if (event.code === "Esc") {
            setValue(props.value ?? "");
            setIsEditing(false);
        }
    };

    const handleBlur = () => {
        if ((props.type === ControlType.URL || props.type === ControlType.EMAIL)
            && ref.current?.value
            && !ref.current?.checkValidity()) {
            ref.current?.reportValidity();
            return;
        }
        setIsEditing(false);
        if (!value && !props.isFormField) {
            setValue(lastSaved);
            return;
        }

        if (value !== lastSaved) {
            setLastSaved(value);
            props.onChange?.(value, replacements);
        }
    };

    useEffect(() => () => {
        handleBlur();
    }, []);

    const handleEditorBlur = useCallback(() => {
        const cleanContent = santitize(htmlValue, {
            stripFormatting: StripFormatting.ALL,
            shouldAllowMedia: false,
        });
        props.onChange?.(cleanContent, editorReplacements);
    }, [htmlValue, editorReplacements, props.onChange]);

    const handleKeyDownForm = (event: React.KeyboardEvent) => {
        if (event.code === "Escape") {
            setValue(lastSaved);
            setIsEditing(false);
        }
        if (props.shouldHandleEnter && event.code === "Enter") {
            event.preventDefault();
            event.stopPropagation();
            handleSubmit();
        }
    };

    if (!(isEditing || props.isEditing) || props.isReadOnly) {
        let display = props.children ?? (!Array.isArray(value) || value.isNotEmpty ? value : undefined);
        if (!props.children && value && props.type === ControlType.MULTI_SELECT_TAGS
            && Array.isArray(value) && value.isNotEmpty) {
            display = (
                <>
                    {(value as string[]).map(row => (
                        <small className="me-2" key={row}>
                            <code>
                                #
                                {row}
                            </code>
                        </small>
                    ))}
                </>
            );
        } else if (value && props.type === ControlType.DATE) {
            display = new Date(value as string).toLocaleDateString(undefined, { timeZone: "UTC" });
        } else if (value && props.type === ControlType.TIME) {
            const date = new Date(value as string);
            display = date.toLocaleTimeString(undefined, { timeZone: "UTC" });
        } else if (value && props.type === ControlType.DATE_TIME) {
            const date = new Date(value as string);
            display = date.toLocaleString(undefined);
        } else if (value && props.type === ControlType.SELECT) {
            display = props.options?.find(val => val.value === value)?.label ?? value;
        } else if (value && props.type === ControlType.FILES) {
            return (
                <div className={Styles["attachment-list"]}>
                    {(value as FieldTypedValueFileInput[])?.map(file => (
                        <Attachment
                            key={file.url}
                            url={file.url as string}
                            size="xs"
                            mimeType={file.contentType as string}
                            title={file.title || file.filename as string}
                        />
                    ))}
                </div>
            );
        } else if (value != null
            && props.type === ControlType.ASYNC_DROPDOWN
            && props.asyncObjectType === AsyncObjectType.Severity) {
            display = (
                <SeveritiesPicker
                    value={props.value as string}
                    isReadonly
                />
            );
        }

        let displaySpan = display as string || (
            <span className={classNames(props.placeholderClassName, theme.isV3
                ? Styles["form-placeholder"]
                : `text-gray-400`)}
            >
                {props.placeholder}
            </span>
        );

        if (props.type === ControlType.EMAIL && value) {
            displaySpan = (
                <a href={`mailto:${value}`} onClick={row => row.stopPropagation()}>
                    {displaySpan}
                </a>
            );
        }
        if (props.type === ControlType.URL && value) {
            displaySpan = (
                <a href={value as string} target="_blank" rel="noreferrer" onClick={row => row.stopPropagation()}>
                    {displaySpan}
                </a>
            );
        }

        return (
            <span
                onDoubleClick={() => setIsEditing(!props.isReadOnly && true)}
                onClick={() => setIsEditing(!props.isReadOnly && true)}
                onKeyDown={handleKeyDown}
                role="textbox"
                data-test="textbox"
                tabIndex={0}
            >
                {displaySpan}
                {!props.isReadOnly && props.editIcon && (
                    <Button
                        onClick={() => setIsEditing(true)}
                        prefixIcon={props.editIcon}
                        variant="none"
                        size="xsmall"
                        className={theme.isV3 ? "" : "p-0"}
                    />
                )}
            </span>
        );
    }

    const setReplacement = (group: ReplacementGroup, row: FormField) => {
        const replacement = `{{${normalizeInput(group.label)}/${normalizeInput(row.name)}}}`;
        const newReplacements = Array.from(replacements);
        newReplacements.push({
            fieldId: row.id,
            groupId: group.id,
            replacement,
        });
        setReplacements(newReplacements);

        if (ref.current) {
            const cursor = ref.current.selectionStart ?? 0;
            const newValue = ref.current.value.substring(0, cursor) + replacement + ref.current.value.substring(cursor);
            setValue(newValue);
            props.onChange?.(newValue, newReplacements);
        }

        setShowReplace(false);
    };

    let control: ReactElement;
    if (props.type === ControlType.LONG_TEXT) {
        control = (
            // eslint-disable-next-line react/jsx-no-useless-fragment
            <>
                {theme.isV3 && props.shouldUseEditor ? (
                    <Editor
                        value={htmlValue as string}
                        onChange={(html, cleanContent, _mentions, newReplacements) => {
                            setHtmlValue(html);
                            setEditorReplacements(newReplacements ?? []);
                        }}
                        onBlur={handleEditorBlur}
                        isRequired={isRequired}
                        hasBorder
                        variables={{
                            valueFormat: "auto",
                            variables: props.replaceableInput ?? [],
                            shouldReplaceVariables: true,
                        }}
                        canFormat={false}
                    />
                ) : (
                    <>
                        <textarea
                            ref={item => {
                                ref.current = item;
                            }}
                            required={isRequired}
                            onChange={event => setValue(event.target.value)}
                            onBlur={handleBlur}
                            placeholder={props.placeholder}
                            value={value as string}
                            onKeyDown={handleKeyDownForm}
                            className={props.inputClassName}
                            readOnly={props.isInputReadOnly}
                        />
                        {
                            props.replaceableInput && (
                                <div className="d-flex justify-content-end">
                                    <Button
                                        size={theme.isV3 ? "small" : undefined}
                                        className={theme.isV3 ? "p-0" : "btn-link text-light p-1"}
                                        title="Add variables to the text that will be replaced
                                            during the execution of this automation."
                                        onClick={() => setShowReplace(true)}
                                        variant={theme.isV3 ? "none" : "none"}
                                        prefixIcon={theme.isV3 ? <Icon.Variable /> : <HiOutlineVariable className="me-1" />}
                                    >
                                        Variables
                                    </Button>
                                </div>
                            )
                        }
                    </>
                )}
            </>
        );
    } else if (props.type === ControlType.BOOLEAN) {
        control = (

            <BooleanSelect
                placeholder={props.placeholder}
                isRequired={props.isRequired}
                input={props.replaceableInput}
                onChange={(selected, display, replace) => {
                    props.onChange?.(selected, replace, display);
                    setIsEditing(false);
                }}
                value={value as boolean}
            />

        );
    } else if (props.type === ControlType.JSON) {
        control = (
            <JsonBuilder
                input={props.replaceableInput}
                replacements={props.replacements}
                schema={props.jsonSchema as WorkflowFormFieldSchema}
                integrationId={props.integrationId}
                onChange={selected => {
                    props.onChange?.(selected);
                    setIsEditing(false);
                }}
                value={value as (string | Record<string, unknown>)}
            />
        );
    } else if (props.type === ControlType.WORKFLOW || props.type === ControlType.WORKFLOW_TICKET) {
        control = (
            <>
                <h4>{props.jsonSchema?.label?.trim()}</h4>
                <p>{props.jsonSchema?.description?.trim()}</p>
                <WorkflowPicker
                    title={props.jsonSchema?.label ?? "Automation"}
                    buttonText={(isRecord(props.jsonSchema?.root) ? props.jsonSchema.root.label : undefined)
                        ?? props.jsonSchema?.label
                        ?? "Automation"}
                    buttonClassName="border rounded px-3 py-2"
                    iconClassName="text-light"
                    workflowId={value as string || undefined}
                    onWorkflowSaved={id => {
                        props.onChange?.(id, [], "Automation");
                    }}
                    type={props.type === ControlType.WORKFLOW ? ActionContextType.GenericNoTicket : ActionContextType.Ticket}
                    input={[
                        {
                            id: "urn:fq:form-field-group:execute-context",
                            label: props.jsonSchema?.label as string,
                            description: props.jsonSchema?.description as string,
                            input: isRecord(props.jsonSchema?.root)
                                ? props.jsonSchema?.root.fields.flatMap(field => (
                                    field.type === FormFieldType.User
                                        ? createUserWorkflowFields(field)
                                        : field
                                )) as FormField[]
                                : undefined,
                        },
                    ]}
                // value={props.value as TicketPriority}
                // isClearable={!props.isRequired}
                // onSelected={(selected, text) => {
                //     props.onChange?.(selected, replacements, text);
                //     setIsEditing(false);
                // }}
                />
            </>
        );
    } else if (props.options && (props.type === ControlType.MULTI_LEVEL_SELECT)) {
        // eslint-disable-next-line no-inner-declarations

        const controls = [];
        let options = props.options as RecursiveOption<unknown>[] | undefined;
        const values = Array.isArray(value) ? value.copy().reverse() : [];

        let index = 0;

        let dynamicChoice: string | undefined;
        const parentValues: string[] = [];

        do {
            const current = values.pop() as string;
            const currentOptions = dynamicChoice ? undefined : options?.find(row => row.value === current);

            controls.push(((
                newIndex: number,
                nextOptions: RecursiveOption<unknown>[] | undefined,
                currentValue: string[],
                currentDynamicChoice: string | undefined,
            ) => (
                <SelectLevel
                    key={newIndex}
                    index={newIndex}
                    labels={props.labels}
                    placeholder={props.placeholder}
                    options={nextOptions}
                    current={currentValue}
                    setValue={handler => {
                        const newValues = handler(newIndex, value, dynamicChoices);
                        props.onChange?.(newValues.value, replacements);
                        setValue(newValues.value);
                        setDynamicChoices(newValues.dynamicContexts as string[]);
                    }}
                    onKeyDown={handleKeyDownForm}
                    dynamicChoiceContext={currentDynamicChoice}
                    integrationId={props.integrationId}
                    parentValues={parentValues}
                    getCurrentForm={props.getCurrentForm}
                    optionsSettings={props.optionsSettings}
                    theme={theme}
                />
            ))(index, options, (value as string[]).slice(index), dynamicChoice || dynamicChoices[index - 1]));
            if (currentOptions) {
                parentValues.push(currentOptions?.value);
            }
            options = currentOptions?.children;
            dynamicChoice = (currentOptions as FormFieldOption)?.dynamicChoiceContext as string
                ?? (currentOptions?.data as FormFieldOption)?.dynamicChoiceContext;
            index += 1;
        } while (options?.isNotEmpty || dynamicChoice);

        control = (
            <div
                onBlur={event => {
                    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
                        setIsEditing(false);
                        // props.onChange?.(value, replacements, options?.find(opt => opt.value === value)?.label as string);
                    }
                }}
            >
                {controls}
            </div>
        );
    } else if ((props.options || props.optionsSettings?.dynamicChoiceContext) && (
        props.type === ControlType.SELECT
        || props.type === ControlType.MULTI_SELECT
        || props.type === ControlType.MULTI_SELECT_TAGS
        || props.type === ControlType.MULTI_LEVEL_SELECT
        || (props.type === ControlType.ASYNC_DROPDOWN && !props.asyncObjectType))) {
        const allOptions = props.options?.filterNull()
            .flatMap(row => (row.children ? row.children : [row]))
            .filter(row => row.value)
            .toIdMap("value");
        const selectValue = allOptions
            ? (Array.isArray(value) ? value : [value]).map(row => allOptions[row as string]).filterNull()
            : [];
        const isMulti = props.type === ControlType.MULTI_SELECT || props.type === ControlType.MULTI_SELECT_TAGS;

        const onChange = (event: readonly Option[] | Option) => {
            let newValue: string | string[];
            if (!Array.isArray(event)
                && (props.type === ControlType.SELECT
                    || props.type === ControlType.MULTI_LEVEL_SELECT
                    || props.type === ControlType.ASYNC_DROPDOWN)) {
                const first = event as Option<never>;
                newValue = first?.value;
            } else {
                newValue = Array.from(Array.isArray(event) ? event : [event]).map(row => row.value);
            }
            setValue(newValue);

            if (props.type === ControlType.SELECT
                || props.type === ControlType.MULTI_SELECT
                || props.type === ControlType.ASYNC_DROPDOWN) {
                setTimeout(() => {
                    props.onChange?.(
                        newValue,
                        replacements,
                        props.options?.find(opt => opt.value === newValue)?.label as string,
                    );
                }, 1);

                setIsEditing(false);
            }
        };
        if (props.optionsSettings?.dynamicChoiceContext) {
            control = (
                <DynamicPicker
                    labels={props.labels}
                    onChange={(newValue, replace, text) => {
                        if (newValue) {
                            setValue(newValue);
                        }
                        props.onChange?.(newValue, replace, text);
                        setIsEditing(false);
                    }}
                    placeholder={props.placeholder}
                    value={value}
                    parentValues={value as string[]}
                    isRequired={isRequired}
                    dynamicChoiceContext={props.optionsSettings?.dynamicChoiceContext as string}
                    optionsSettings={props.optionsSettings}
                    integrationId={props.integrationId as string}
                    type={props.type}
                    getCurrentForm={props.getCurrentForm}
                    isFormField={props.isFormField}
                />
            );
        } else {
            control = (
                <Select
                    onChange={onChange}
                    minRequired={isRequired ? 1 : undefined}
                    isClearable={!isRequired}
                    isMulti={isMulti}
                    onBlur={handleBlur}
                    onKeyDown={handleKeyDownForm}
                    placeholder={props.placeholder}
                    value={isMulti
                        ? selectValue as Option<never>[]
                        : selectValue?.firstElement as Option<never>}
                    options={props.options?.filterNull().map(opt => (opt.children ? ({
                        label: opt.label,
                        options: opt.children?.map(child => ({
                            label: child.label,
                            value: child.value,
                        })),
                    }) : {
                        label: opt.label,
                        value: opt.value,
                    })) as RecursiveOption<never>[]}
                />
            );
        }
    } else if (props.type === ControlType.CHECKBOX_LIST) {
        const setCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
            const selected = event.target.value;
            const isChecked = event.target.checked;
            setValue(current => {
                const list = Array.isArray(current) ? current as string[] : [];
                if (isChecked) {
                    list.push(selected);
                } else {
                    const index = list.findIndex(row => row === selected);
                    list.splice(index, 1);
                }
                props.onChange?.(list, replacements);
                return list;
            });
        };
        control = (
            <ul className="list-group">
                {props.options?.map(opt => (
                    <label className="list-group-item" key={opt.value}>
                        <input type="checkbox" value={opt.value} className="me-1" onChange={setCheckbox} />
                        {" "}
                        {opt.label}
                    </label>
                ))}
            </ul>
        );
    } else if (props.type === ControlType.TIME && theme.isV3) {
        control = (
            <TimePicker
                value={props.value as number}
                onChange={timeValue => {
                    props.onChange?.(timeValue?.toUTCString(), undefined, timeValue?.toString());
                    setIsEditing(false);
                }}
            />
        );
    } else if (props.type === ControlType.ASYNC_DROPDOWN || props.type === ControlType.USER || props.type === ControlType.USERS) {
        if (props.asyncObjectType === AsyncObjectType.Priority) {
            control = (
                <PriorityPicker
                    value={props.value as TicketPriority}
                    isClearable={!props.isRequired}
                    onSelected={(selected, text) => {
                        props.onChange?.(selected, replacements, text);
                        setIsEditing(false);
                    }}
                />
            );
        } else if (props.asyncObjectType === AsyncObjectType.Group) {
            control = (
                <UserGroups
                    value={props.value as string}
                    isClearable={!props.isRequired}
                    onSelected={(selected, display) => {
                        props.onChange?.(selected, replacements, display);
                        setIsEditing(false);
                    }}
                />
            );
        } else if (props.asyncObjectType === AsyncObjectType.Queue) {
            control = (
                <Queues
                    value={props.value as string}
                    isClearable={!props.isRequired}
                    onSelected={(selected, display) => {
                        props.onChange?.(selected, replacements, display);
                        setIsEditing(false);
                    }}
                />
            );
        } else if (props.asyncObjectType === AsyncObjectType.TicketState) {
            control = (
                <TicketStates
                    value={props.value as string}
                    isClearable={!props.isRequired}
                    onSelected={(selected, display) => {
                        props.onChange?.(selected, replacements, display);
                        setIsEditing(false);
                    }}
                />
            );
        } else if (props.asyncObjectType === AsyncObjectType.User || props.type === ControlType.USER) {
            control = (
                <Users
                    value={props.value as string}
                    input={props.replaceableInput}
                    replacements={replacements}
                    isClearable={!props.isRequired}
                    canCreate={props.canCreate}
                    onSelected={(selected, display, replace) => {
                        props.onChange?.(selected, replace ? replacements.concat(replace) : replacements, display);
                        setIsEditing(false);
                    }}
                />
            );
        } else if (props.type === ControlType.USERS) {
            control = (
                <Users
                    isMulti
                    input={props.replaceableInput}
                    isClearable={!props.isRequired}
                    replacements={replacements}
                    value={props.value as string[]}
                    canCreate={props.canCreate}
                    onSelected={(selected, display, replace) => {
                        props.onChange?.(selected, replace ? replacements.concat(replace) : replacements, display?.join(", "));
                        setIsEditing(false);
                    }}
                />
            );
        } else if (props.asyncObjectType === AsyncObjectType.Severity) {
            control = (
                <SeveritiesPicker
                    value={props.value as string}
                    isClearable={!props.isRequired}
                    onSelected={(selected, display) => {
                        props.onChange?.(selected, replacements, display);
                        setIsEditing(false);
                    }}
                />
            );
        } else if (props.asyncObjectType === AsyncObjectType.SlaTier) {
            control = (
                <SlaTierPicker
                    value={props.value as string}
                    isClearable={!props.isRequired}
                    onSelected={(selected, display) => {
                        props.onChange?.(selected, replacements, display);
                        setIsEditing(false);
                    }}
                />
            );
        } else if (props.asyncObjectType === AsyncObjectType.ChatStatus) {
            control = (
                <ChatStatusPicker
                    value={props.value as ChatStatus}
                    isClearable={!props.isRequired}
                    onChange={(selected, display) => {
                        props.onChange?.(selected, replacements, display);
                        setIsEditing(false);
                    }}
                />
            );
        } else if (props.asyncObjectType === AsyncObjectType.RequestType) {
            control = (
                <RequestTypes
                    value={props.value as string}
                    minRequired={props.isRequired ? 1 : 0}
                    onSelected={(selected, display) => {
                        props.onChange?.(selected, replacements, display);
                        setIsEditing(false);
                    }}
                />
            );
        } else if (props.asyncObjectType === AsyncObjectType.RequestTypes) {
            control = (
                <RequestTypes
                    value={props.value as string[]}
                    isMulti
                    minRequired={props.isRequired ? 1 : 0}
                    onSelected={(selected, display) => {
                        props.onChange?.(selected, undefined, display?.join(", "));
                        setIsEditing(false);
                    }}
                />
            );
        } else if (props.asyncObjectType === AsyncObjectType.Action) {
            control = (
                <RequestTypes
                    value={props.value as string}
                    minRequired={props.isRequired ? 1 : 0}
                    type={FormType.Action}
                    onSelected={(selected, display) => {
                        props.onChange?.(selected, replacements, display);
                        setIsEditing(false);
                    }}
                />
            );
        } else if (props.asyncObjectType === AsyncObjectType.Tags) {
            control = (
                <Tags
                    value={props.value as string[]}
                    isMulti
                    isClearable={!props.isRequired}
                    onSelected={selected => {
                        props.onChange?.(selected, replacements, selected?.join(", "));
                        setIsEditing(false);
                    }}
                />
            );
        } else if (props.asyncObjectType === AsyncObjectType.ChannelTags) {
            control = (
                <ChannelTags
                    value={props.value as string[]}
                    isMulti
                    isClearable={!props.isRequired}
                    onSelected={selected => {
                        props.onChange?.(selected, replacements, selected?.join(", "));
                        setIsEditing(false);
                    }}
                />
            );
        } else if (props.asyncObjectType === AsyncObjectType.DeflectionSource) {
            control = (
                <DeflectionSource
                    value={props.value as string}
                    isClearable={!props.isRequired}
                    onSelected={(selected, display) => {
                        props.onChange?.(selected, replacements, display);
                        setIsEditing(false);
                    }}
                />
            );
        } else if (props.asyncObjectType === AsyncObjectType.Email) {
            control = (
                <EmailAliases
                    value={props.value as string}
                    isClearable={!props.isRequired}
                    onSelected={(selected, display) => {
                        props.onChange?.(selected, replacements, display);
                        setIsEditing(false);
                    }}
                />
            );
        } else if (props.asyncObjectType === AsyncObjectType.Timezone) {
            control = (
                <TimezoneSelect
                    value={props.value as string}
                    isRequired={props.isRequired}
                    onChange={selected => {
                        props.onChange?.(selected, replacements);
                        setIsEditing(false);
                    }}
                />
            );
        } else if (props.asyncObjectType === AsyncObjectType.Channel) {
            control = (
                <ConversationSources
                    value={props.value as string}
                    sourceTypes={[ConversationSourceType.SlackChannel, ConversationSourceType.MicrosoftTeams]}
                    minRequired={props.isRequired ? 1 : 0}
                    onSelected={selected => {
                        props.onChange?.(selected, replacements);
                        setIsEditing(false);
                    }}
                />
            );
        } else if (props.asyncObjectType === AsyncObjectType.FieldInContext
            || props.asyncObjectType === AsyncObjectType.FieldsInContext) {
            const childField = props.replaceableInput?.flatMap(row => row.input)
                .filterNull()
                .find(row => row.asyncObjectType === AsyncObjectType.FieldInContext);
            const convertedOptions = childField?.options?.map(opt => ({
                label: opt.label as string,
                value: opt.value as string,
            })) ?? [];
            let currentValue: Option<unknown> | Option<unknown>[] | undefined;
            if (props.asyncObjectType === AsyncObjectType.FieldInContext) {
                currentValue = convertedOptions?.find(opt => opt.value === props.value as string);
            } else if (Array.isArray(props.value)) {
                const set = new Set(props.value as string[]);
                currentValue = convertedOptions?.filter(opt => set.has(opt.value));
            }

            control = (
                <Select
                    isMulti={props.asyncObjectType === AsyncObjectType.FieldsInContext}
                    value={currentValue}
                    options={convertedOptions}
                    onChange={selected => {
                        const selectedValue = Array.isArray(selected)
                            ? selected.map((row: Option<unknown>) => row.value)
                            : (selected as Option<unknown>).value;
                        const label = Array.isArray(selected)
                            ? selected.map((row: Option<unknown>) => row.label).join(", ")
                            : (selected as Option<unknown>).label as string;

                        props.onChange?.(selectedValue, replacements, label);
                        setIsEditing(false);
                    }}
                />
            );
        } else {
            control = (
                <div>Unknown </div>
            );
        }
    } else {
        const setValueAndUpdate = props.type !== ControlType.SEARCH_TEXT ? setValue : (text: string) => {
            setValue(text);

            if (searchTimeout) {
                clearTimeout(searchTimeout);
            }
            setSearchTimeout(setTimeout(() => {
                props.onChange?.(text, replacements);
            }, 500));
        };

        let inputValue = value;
        if (inputValue instanceof Date) {
            if (props.type === ControlType.DATE) {
                inputValue = toUTCInputDate(inputValue);
            } else if (props.type === ControlType.TIME) {
                inputValue = toUTCInputHourMinute(inputValue);
            } else if (props.type === ControlType.DATE_TIME) {
                inputValue = toInputDateTime(inputValue);
            }
        }

        const selectedReplaceId = props.replacements?.find(replace => replace.replacement === inputValue);
        const selectedReplaceGroup = selectedReplaceId
            ? props.replaceableInput?.find(group => group.id === selectedReplaceId.groupId)
            : undefined;
        const selectedReplace = selectedReplaceGroup?.input?.find(input => input.id === selectedReplaceId?.fieldId);

        const showReplacements = props.replaceableInput?.isNotEmpty
            && (props.type === ControlType.DATE
                || props.type === ControlType.TIME
                || props.type === ControlType.DATE_TIME
                || props.type === ControlType.NUMBER
                || props.type === ControlType.DECIMAL
                || props.type === ControlType.URL
                || props.type === ControlType.EMAIL);

        const applicableReplacements = !showReplacements ? undefined : props.replaceableInput?.map(row => ({
            ...row,
            input: row.input?.filter(input => FORM_FIELD[input.type] === props.type),
        })).filter(row => row.input?.isNotEmpty);

        control = theme.isV3 && props.shouldUseEditor ? (
            <Editor
                value={htmlValue as string}
                onChange={(html, cleanContent, _mentions, newReplacements) => {
                    setHtmlValue(html);
                    setEditorReplacements(newReplacements ?? []);
                }}
                onBlur={handleEditorBlur}
                isRequired={isRequired}
                hasBorder
                variables={{
                    valueFormat: "auto",
                    shouldReplaceVariables: true,
                    variables: props.replaceableInput ?? [],
                }}
                canFormat={false}
                isSingleLine
            />
        )
            : (
                <div className="d-flex justify-content-between">
                    {(selectedReplaceGroup && selectedReplace && showReplacements) ? (
                        <div className="alert alert-light px-3 py-1 w-100 mb-0">
                            {props.replacementInsertLabel}
                            {selectedReplaceGroup.label}
                            {" "}
                            /
                            {" "}
                            {selectedReplace.name}
                            <button
                                type="button"
                                className="btn btn-none p-1 align-baseline"
                                onClick={() => props.onChange?.(undefined, [])}
                                aria-label="Close icon"
                            >
                                <GrFormClose className="text-light" />
                            </button>
                        </div>
                    ) : (
                        <input
                            ref={item => {
                                ref.current = item;
                            }}
                            required={isRequired}
                            type={FORM_FIELD_TO_TYPE[props.type ?? ControlType.TEXT]}
                            step={props.type === ControlType.DECIMAL ? 0.000001 : undefined}
                            onChange={event => {
                                const rawValue = event.target.value;
                                const validatedValue = rawValue && props.type === ControlType.NUMBER
                                    ? Number.parseInt(rawValue, 10).toString()
                                    : rawValue;
                                setValueAndUpdate(validatedValue);
                            }}
                            onBlur={handleBlur}
                            placeholder={props.placeholder}
                            value={inputValue as string}
                            onKeyDown={handleKeyDownForm}
                            className={props.inputClassName}
                            readOnly={props.isInputReadOnly}
                            maxLength={props?.maxLength}
                        />
                    )}

                    {props.replaceableInput?.isNotEmpty && (props.type === ControlType.TEXT || !props.type) && (
                        <Button
                            size={theme.isV3 ? "small" : "small"}
                            className={theme.isV3
                                ? ""
                                : (`btn-outline text-light ms-2 px-2 py-1 my-2 ${props.variableClassName}`)}
                            title="Add variables to the text that will be replaced during the execution of this automation."
                            onClick={() => setShowReplace(true)}
                            variant={theme.isV3 ? "ghost-gray" : "none"}
                            prefixIcon={theme.isV3 ? <Icon.Variable /> : <HiOutlineVariable />}
                            aria-label="Replace variables"
                        />
                    )}
                    {applicableReplacements?.isNotEmpty && (
                        <Dropdown as={ButtonGroup}>

                            <Dropdown.Toggle
                                split
                                variant="none"
                                className="py-2  rounded-end"
                            >
                                {theme.isV3 && <Icon.Variable size="small" variant="default" />}
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                                <Dropdown.Header>
                                    Default
                                </Dropdown.Header>
                                <Dropdown.Item onClick={() => props.onChange?.(undefined, [])}>
                                    Manual
                                </Dropdown.Item>
                                {applicableReplacements?.map(group => (
                                    <>
                                        <Dropdown.Header key={group.id}>
                                            {group.label}
                                        </Dropdown.Header>
                                        {group.input?.map(input => (
                                            <Dropdown.Item
                                                key={input.id}
                                                onClick={() => {
                                                    const replacement = `{{${normalizeInput(group.label)}/`
                                                    + `${normalizeInput(input.name)}}}`;
                                                    props.onChange?.(replacement, [{
                                                        fieldId: input.id,
                                                        groupId: group.id,
                                                        replacement,
                                                    }]);
                                                }}
                                            >
                                                {input.name}
                                            </Dropdown.Item>
                                        ))}
                                    </>
                                ))}

                            </Dropdown.Menu>

                        </Dropdown>
                    )}
                </div>
            );
    }

    if (props.replaceableInput?.isNotEmpty) {
        control = (
            <>
                {control}
                {!theme.isV3 && (
                    <Confirm
                        shouldShow={isShowReplace}
                        onHide={() => setShowReplace(false)}
                        dialogClassName="mt-2"
                        keyboardContext="variables"
                    >
                        <div className={Styles.replacements}>
                            <h5>
                                Variables
                            </h5>
                            <p>
                                Set variables
                            </p>
                            {props.replaceableInput?.map(group => (
                                <div className="mt-3 border rounded p-2" key={group.id}>
                                    <h6>
                                        {group.label}
                                    </h6>
                                    <p>
                                        {group.description}
                                    </p>
                                    <div className="d-flex flex-wrap ">
                                        {group.input?.filter(row => !BLOCKED_TEXT_REPLACEMENTS.has(row.type)).map(row => (
                                            <button
                                                key={row.id}
                                                className="btn btn-outline border border-1 p-2 m-1"
                                                type="button"
                                                onClick={() => setReplacement(group, row)}
                                            >
                                                {row.name}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </Confirm>
                )}

            </>
        );
    }

    if (props.isFormField) {
        return (
            <div className="w-100">
                {control}
            </div>
        );
    }
    return (
        <form onSubmit={handleSubmit} className="w-100">
            {control}
        </form>
    );
}

EditableControl.defaultProps = {
    value: undefined,
    placeholder: "Untitled",
    placeholderClassName: "",
    onChange: undefined,
    isReadOnly: false,
    options: undefined,
    type: ControlType.TEXT,
    children: undefined,
    labels: undefined,
    isEditing: false,
    shouldFocus: true,
    isFormField: false,
    inputClassName: "bottom-line",
    variableClassName: "",
    isRequired: true,
    asyncObjectType: undefined,
    replaceableInput: undefined,
    replacements: undefined,
    canCreate: false,
    getCurrentForm: undefined,
    maxLength: undefined,

    optionsSettings: undefined,
    integrationId: undefined,
    isInputReadOnly: undefined,
    replacementInsertLabel: "Insert from ",
    editIcon: undefined,
    jsonSchema: undefined,
    shouldHandleEnter: undefined,
    shouldUseEditor: false,
};
